import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  User,
  type MultiFactorError,
} from 'firebase/auth'
import { isSecondStepRequired } from '../twoFactor'
import { auth, db } from '../../firebase'
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { UserRoles } from '../../constants/userRoles'
import type { UserRole } from '../../constants/userRoles'
import {
  extractNameFromDisplayNameOrEmail,
  validateMinimalRegistration,
} from '../userDataValidation'
import { createEnhancedUserDocument } from './user.service'
import { logger } from '../logger'
import { securityService } from '../security'
import { InviteOnlyMessages } from '../../constants/messages'
import { noInvitationEmail } from './inviteOnly'
import { FIREBASE_ERROR_PATTERNS } from '../../types/errorTypes'
import { requestAccountEmail, sendConfirmEmail } from '../accountEmail'

interface AuthResult {
  success: boolean
  user?: User
  error?: string
  /** Firebase's error code, when `error` is our own wording (PB-90). */
  code?: string
  /** Set when Firebase asks for the second sign-in step (PB-62). */
  secondStep?: MultiFactorError
}

// Minimal registration interface for Phase 1
export interface MinimalRegistrationData {
  firstName: string
  lastName: string
  email: string
  password: string
  phoneNumber?: string
  role?: UserRole
  accountType?: string
  corporateAccountId?: string | null // For invitation acceptance flow
  invitationId?: string // Invitation acceptance: proves membership (RS-2)
}

// Register a new user with minimal data (Phase 1 approach)
export const registerWithMinimalData = async (
  data: MinimalRegistrationData,
  createdBy: string | null = null,
  // skipUserDocument: the server writes the record (super admin, PB-72)
  options?: { skipEmailVerification?: boolean; skipUserDocument?: boolean }
): Promise<AuthResult> => {
  try {
    // Validate minimal registration data
    const validation = validateMinimalRegistration({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
    })

    if (!validation.isValid) {
      return { success: false, error: validation.errors.join(', ') }
    }

    // Create the user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      data.email,
      data.password
    )
    const user = userCredential.user

    // Send email verification (skip for invitation acceptance — handled by Cloud Function)
    if (!options?.skipEmailVerification) {
      await sendConfirmEmail()
    }

    // Create enhanced user document in Firestore with minimal data
    if (!options?.skipUserDocument) {
      await createEnhancedUserDocument(
        user,
        data.role || UserRoles.TENANT,
        {
          firstName: data.firstName,
          lastName: data.lastName,
          ...(data.phoneNumber && { phoneNumber: data.phoneNumber }),
        },
        createdBy,
        // Pass corporateAccountId if provided (for invitation acceptance flow),
        // with the invitation that proves membership (RS-2 step 5)
        data.corporateAccountId !== undefined
          ? {
              corporateAccountId: data.corporateAccountId,
              ...(data.invitationId && { invitationId: data.invitationId }),
            }
          : undefined
      )
    }

    securityService.logAuthSuccess(user, 'email')
    return { success: true, user }
  } catch (error) {
    logger.error('Error registering user with minimal data', error, {
      email: data.email,
    })
    securityService.logAuthFailure((error as Error).message, data.email)
    // Refused by the account-creation check: no invitation (PB-64)
    const refusedEmail = noInvitationEmail(error)
    if (refusedEmail !== null) {
      return {
        success: false,
        error: InviteOnlyMessages.noInvitationTitle(refusedEmail || data.email),
      }
    }
    return { success: false, error: (error as Error).message }
  }
}

// Register a new user with email and password (legacy compatibility)
export const registerWithEmail = async (
  email: string,
  password: string,
  role: UserRole = UserRoles.TENANT,
  createdBy: string | null = null
): Promise<AuthResult> => {
  // Extract names from email (since we don't have display name yet)
  const { firstName, lastName } = extractNameFromDisplayNameOrEmail('', email)

  // Delegate to registerWithMinimalData
  return registerWithMinimalData(
    {
      firstName,
      lastName,
      email,
      password,
      role,
    },
    createdBy
  )
}

// Sign in with email and password
// Update lastLoginAt on successful sign-in (fire-and-forget)
export const updateLastLogin = (uid: string) => {
  updateDoc(doc(db, 'users', uid), { lastLoginAt: serverTimestamp() }).catch(
    err => logger.warn('Failed to update lastLoginAt', err)
  )
}

export const signInWithEmail = async (
  email: string,
  password: string
): Promise<AuthResult> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    )
    securityService.logAuthSuccess(userCredential.user, 'email')
    updateLastLogin(userCredential.user.uid)
    return { success: true, user: userCredential.user }
  } catch (error) {
    if (isSecondStepRequired(error)) {
      return {
        success: false,
        error: 'Second step required',
        secondStep: error,
      }
    }
    logger.error('Error signing in', error, { email })
    securityService.logAuthFailure((error as Error).message, email)
    // Plain words for the errors we know; Firebase's own text otherwise
    const code = (error as { code?: string }).code ?? ''
    return {
      success: false,
      code: code || undefined,
      error:
        FIREBASE_ERROR_PATTERNS[code]?.userMessage ?? (error as Error).message,
    }
  }
}

// Sign out
export const logOut = async () => {
  try {
    const currentUser = auth.currentUser
    console.info('[AuthRestore] signOut: logOut', new Error().stack)
    await signOut(auth)
    if (currentUser) {
      securityService.logLogout(currentUser.uid)
    }
    return { success: true }
  } catch (error) {
    logger.error('Error signing out', error)
    return { success: false, error: (error as Error).message }
  }
}

// Send password reset email
export const sendPasswordResetEmail = async (
  email: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await requestAccountEmail('passwordReset', { email })
    return { success: true }
  } catch (error) {
    logger.error('Error sending password reset email', error, { email })
    return { success: false, error: (error as Error).message }
  }
}
